/**
 * Produktgeometrie einer Größe. Spiegel von api `App\Service\Editor\Geometry\ProductGeometry::toArray()`.
 * Alle Maße in Millimetern. `trim` = Endformat, `full` = Endformat + 2 × Beschnitt.
 */
export type ShapeKind = 'rect' | 'rounded' | 'circle' | 'hexagon' | 'custom'

export type HexOrientation = 'flat' | 'pointy'

export type ShapeParams = {
  /** Sechseck: Kante oben (flat, Standard) oder Spitze oben (pointy) */
  orientation?: HexOrientation
  /** custom: Asset-ID der SVG-Maske (Backend) */
  assetId?: string
  /** custom: absolute URL der (sanitisierten) SVG-Maske */
  maskSvgUrl?: string
  [key: string]: unknown
}

export type ProductGeometry = {
  shape: ShapeKind
  cornerRadiusMm: number
  bleedMm: number
  safeMarginMm: number
  dpi: number
  trimWidthMm: number
  trimHeightMm: number
  fullWidthMm: number
  fullHeightMm: number
  shapeParams?: ShapeParams | null
}

/** Rechteck in beliebiger Einheit (mm oder px), Ursprung oben links. */
export type Rect = { x: number; y: number; w: number; h: number }

export const DEFAULT_GEOMETRY: ProductGeometry = {
  shape: 'rect',
  cornerRadiusMm: 0,
  bleedMm: 0,
  safeMarginMm: 0,
  dpi: 300,
  trimWidthMm: 0,
  trimHeightMm: 0,
  fullWidthMm: 0,
  fullHeightMm: 0,
  shapeParams: null,
}

const SHAPES: ShapeKind[] = ['rect', 'rounded', 'circle', 'hexagon', 'custom']

export function normalizeShape(value: unknown): ShapeKind {
  const s = String(value ?? '').trim().toLowerCase()
  return (SHAPES as string[]).includes(s) ? (s as ShapeKind) : 'rect'
}

function num(value: unknown, fallback = 0): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Tolerant aus beliebigem JSON (Bootstrap sizeOption.geometry, preset.size.geometry,
 * preset.size {widthMm,heightMm,bleedMm}) eine vollständige Geometrie bauen.
 * Fehlende trim-Maße können über `fallbackTrim` (z. B. aus size.widthMm/heightMm) ergänzt werden.
 */
export function normalizeGeometry(
  raw: unknown,
  fallbackTrim?: { widthMm?: number | null; heightMm?: number | null } | null,
): ProductGeometry {
  const g = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const bleedMm = Math.max(0, num(g.bleedMm))
  let trimW = Math.max(0, num(g.trimWidthMm))
  let trimH = Math.max(0, num(g.trimHeightMm))
  if (!(trimW > 0) || !(trimH > 0)) {
    trimW = Math.max(0, num(fallbackTrim?.widthMm))
    trimH = Math.max(0, num(fallbackTrim?.heightMm))
  }
  const fullW = num(g.fullWidthMm) > 0 ? num(g.fullWidthMm) : trimW + 2 * bleedMm
  const fullH = num(g.fullHeightMm) > 0 ? num(g.fullHeightMm) : trimH + 2 * bleedMm
  const params = g.shapeParams && typeof g.shapeParams === 'object' && !Array.isArray(g.shapeParams)
    ? (g.shapeParams as ShapeParams)
    : null

  return {
    shape: normalizeShape(g.shape),
    cornerRadiusMm: Math.max(0, num(g.cornerRadiusMm)),
    bleedMm,
    safeMarginMm: Math.max(0, num(g.safeMarginMm)),
    dpi: Math.max(72, Math.min(600, Math.round(num(g.dpi, 300)) || 300)),
    trimWidthMm: trimW,
    trimHeightMm: trimH,
    fullWidthMm: fullW,
    fullHeightMm: fullH,
    shapeParams: params && Object.keys(params).length ? params : null,
  }
}

export function geometryHasGuides(g: ProductGeometry | null | undefined): boolean {
  if (!g) return false
  return g.shape !== 'rect' || g.bleedMm > 0 || g.safeMarginMm > 0
}
